import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SearchBar from '@/components/feature/SearchBar';
import ResourceCard from '@/components/base/ResourceCard';
import Pagination from '@/components/base/Pagination';
import FilterPanel from '@/components/feature/FilterPanel';
import { categoryMeta, getCategoryName, hotResources, resources } from '@/mocks/resources';
import { usePageMeta } from '@/hooks/usePageMeta';

const PAGE_SIZE = 8;

export default function Search() {
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get('q') || '').trim();

  usePageMeta(
    keyword ? `搜索“${keyword}” - 国语对白做受69` : '搜索影音资源 - 国语对白做受69',
    keyword
      ? `为你找到与“${keyword}”相关的国语对白影音资源，支持按分类、地区、年份、类型进一步筛选。`
      : '输入关键词搜索国语对白影音资源，支持按作品名称、导演、主演、类型、地区检索。',
    `${keyword}${keyword ? ',' : ''}国语对白做受69,影音资源搜索,国语影视`
  );

  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [regionSelected, setRegionSelected] = useState<string[]>([]);
  const [yearSelected, setYearSelected] = useState<number[]>([]);
  const [genreSelected, setGenreSelected] = useState<string[]>([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setActiveCategory('all');
    setRegionSelected([]);
    setYearSelected([]);
    setGenreSelected([]);
  }, [keyword]);

  const toggleValue = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  const matched = useMemo(() => {
    if (!keyword) return [];
    const lower = keyword.toLowerCase();
    return resources.filter((item) => {
      const haystack = [
        item.title,
        item.director,
        ...item.cast,
        ...item.genres,
        item.region,
        getCategoryName(item.category),
        item.synopsis,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(lower);
    });
  }, [keyword]);

  const filtered = useMemo(() => {
    let list = matched;
    if (activeCategory !== 'all') {
      list = list.filter((item) => item.category === activeCategory);
    }
    if (regionSelected.length) {
      list = list.filter((item) => regionSelected.includes(item.region));
    }
    if (yearSelected.length) {
      list = list.filter((item) => yearSelected.includes(item.year));
    }
    if (genreSelected.length) {
      list = list.filter((item) =>
        item.genres.some((genre) => genreSelected.includes(genre))
      );
    }
    return [...list].sort((a, b) => b.views - a.views);
  }, [matched, activeCategory, regionSelected, yearSelected, genreSelected]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetFilters = () => {
    setActiveCategory('all');
    setRegionSelected([]);
    setYearSelected([]);
    setGenreSelected([]);
    setPage(1);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-100">
      <Navbar />
      <main className="mx-auto w-full max-w-[1360px] flex-1 px-4 py-8 md:px-8 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7">
          <h1 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            {keyword ? (
              <>
                搜索 “<span className="text-primary-600">{keyword}</span>” 的结果
              </>
            ) : (
              '搜索影音资源'
            )}
          </h1>
          <p className="mt-1.5 text-sm text-foreground-600">
            支持按作品名称、导演、主演、类型、地区等关键词搜索
          </p>
          <div className="mt-4 max-w-2xl">
            <SearchBar size="lg" defaultValue={keyword} placeholder="输入关键词，搜索国语影音资源…" />
          </div>
        </div>

        {!keyword ? (
          <div className="mt-10 flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-16 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
              <i className="ri-search-line" />
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-950">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              例如“悬疑”“中国大陆”“纪录片”或具体的作品名称。
            </p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[260px_1fr]">
            <aside className="flex flex-col gap-4 lg:sticky lg:top-24 lg:self-start">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="mb-3 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-apps-2-line text-primary-600" />
                  按分类筛选
                </h3>
                <div className="flex flex-col gap-1.5">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveCategory('all');
                      setPage(1);
                    }}
                    className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-label transition-colors ${
                      activeCategory === 'all'
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-800 hover:bg-background-100'
                    }`}
                  >
                    全部
                    <span className="text-xs opacity-80">{matched.length}</span>
                  </button>
                  {categoryMeta.map((cat) => {
                    const count = matched.filter((item) => item.category === cat.slug).length;
                    return (
                      <button
                        key={cat.slug}
                        type="button"
                        onClick={() => {
                          setActiveCategory(cat.slug);
                          setPage(1);
                        }}
                        className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm font-label transition-colors ${
                          activeCategory === cat.slug
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-800 hover:bg-background-100'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <i className={cat.icon} />
                          {cat.name}
                        </span>
                        <span className="text-xs opacity-80">{count}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <FilterPanel
                regionSelected={regionSelected}
                yearSelected={yearSelected}
                genreSelected={genreSelected}
                onToggleRegion={(v) => {
                  setRegionSelected((prev) => toggleValue(prev, v));
                  setPage(1);
                }}
                onToggleYear={(v) => {
                  setYearSelected((prev) => toggleValue(prev, v));
                  setPage(1);
                }}
                onToggleGenre={(v) => {
                  setGenreSelected((prev) => toggleValue(prev, v));
                  setPage(1);
                }}
                onReset={resetFilters}
              />
            </aside>

            <div>
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-foreground-600">
                  找到 <span className="font-semibold text-primary-600">{filtered.length}</span> 个匹配资源
                  {totalPages > 1 && ` · 第 ${page}/${totalPages} 页`}
                </p>
              </div>

              {pageItems.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                    {pageItems.map((item) => (
                      <ResourceCard key={item.id} resource={item} />
                    ))}
                  </div>
                  <div className="mt-10">
                    <Pagination
                      current={page}
                      total={totalPages}
                      onChange={(next) => {
                        setPage(next);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                    />
                  </div>
                </>
              ) : (
                <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-6">
                  <div className="flex flex-col items-center py-10 text-center">
                    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-500">
                      <i className="ri-emotion-sad-line" />
                    </span>
                    <h3 className="mt-5 font-heading text-lg font-bold text-foreground-950">
                      未找到相关资源
                    </h3>
                    <p className="mt-2 max-w-sm text-sm text-foreground-600">
                      没有与 “{keyword}” 匹配的内容，试试更换关键词或减少筛选条件。
                    </p>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-5 inline-flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
                    >
                      <i className="ri-refresh-line" />
                      重置筛选
                    </button>
                  </div>

                  <div className="mt-4 border-t border-background-200 pt-6">
                    <h3 className="mb-4 flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                      <i className="ri-fire-line text-accent-600" />
                      热门资源推荐
                    </h3>
                    <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-4">
                      {hotResources.slice(0, 4).map((item) => (
                        <ResourceCard key={item.id} resource={item} />
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}